/************************************************************
 * Primitive Definitions
 */

const __COLOR__ = {
  //
  // Named
  //
  black: '#000000',
  white: '#ffffff',

  //
  // Numbered
  //
  // Blue
  blue100: '#dbeafe',
  blue200: '#bfdbfe',
  blue300: '#60a5fa',
  blue400: '#2563eb',
  blue500: '#1e40af',
  // Green
  green100: '#ecfdf5',
  green200: '#d1fae5',
  green300: '#a7f3d0',
  green400: '#10b981',
  green500: '#048059',
  // Grey
  grey100: '#ffffff',
  grey200: '#faf9f7',
  grey300: '#e5e4e1',
  grey400: '#a6a5a2',
  grey500: '#807f7d',
  grey600: '#4d4c4b',
  grey700: '#292928',
  grey800: '#1a1919',
  grey900: '#000000',
  // Lime
  lime100: '#f7fee7',
  lime200: '#ecfccb',
  lime300: '#bef264',
  lime400: '#65a30d',
  lime500: '#4d7c0f',
  // Purple
  purple100: '#f3e8ff',
  purple200: '#d8b4fe',
  purple300: '#c084fc',
  purple400: '#9333ea',
  purple500: '#6b21a8',
  // Red
  red100: '#ffe3dd',
  red200: '#e89180',
  red300: '#d94123',
  red400: '#a62a17',
  red500: '#802213',
  // Yellow
  yellow100: '#fef9c3',
  yellow200: '#fef08a',
  yellow300: '#fadd46',
  yellow400: '#f5c815',
  yellow500: '#a8890e',
  yellow600: '#765707',
} as const

/************************************************************
 * Semantic Definitions
 */
const __SEMANTIC_COLOR__ = {
  // Action
  action100: __COLOR__.purple100,
  action200: __COLOR__.purple200,
  action300: __COLOR__.purple300,
  action400: __COLOR__.purple400,
  action500: __COLOR__.purple500,
  // Alert
  alert100: __COLOR__.red100,
  alert200: __COLOR__.red200,
  alert300: __COLOR__.red300,
  alert400: __COLOR__.red400,
  alert500: __COLOR__.red500,
  // Info
  info100: __COLOR__.blue100,
  info200: __COLOR__.blue200,
  info300: __COLOR__.blue300,
  info400: __COLOR__.blue400,
  info500: __COLOR__.blue500,
  // Neutrals
  neutral100: __COLOR__.grey100,
  neutral200: __COLOR__.grey200,
  neutral300: __COLOR__.grey300,
  neutral400: __COLOR__.grey400,
  neutral500: __COLOR__.grey500,
  neutral600: __COLOR__.grey600,
  neutral700: __COLOR__.grey700,
  neutral800: __COLOR__.grey800,
  neutral900: __COLOR__.grey900,
  // Success
  success100: __COLOR__.green100,
  success200: __COLOR__.green200,
  success300: __COLOR__.green300,
  success400: __COLOR__.green400,
  success500: __COLOR__.green500,
  // Warning
  warning100: __COLOR__.yellow100,
  warning200: __COLOR__.yellow200,
  warning300: __COLOR__.yellow300,
  warning400: __COLOR__.yellow400,
  warning500: __COLOR__.yellow500,
  warning600: __COLOR__.yellow600,
} as const

/************************************************************
 * Feature Definitions
 */

/**
 * Button
 */
const __BUTTON_COLOR__ = {
  // Background
  buttonPrimary: __SEMANTIC_COLOR__.action400,
  buttonSecondary: __SEMANTIC_COLOR__.neutral600,
  buttonTertiary: __SEMANTIC_COLOR__.neutral100,
  buttonAlert: __SEMANTIC_COLOR__.alert400,
  // Background Hover
  buttonPrimary_hover: __SEMANTIC_COLOR__.action500,
  buttonSecondary_hover: __SEMANTIC_COLOR__.neutral800,
  buttonTertiary_hover: __SEMANTIC_COLOR__.neutral200,
  buttonAlert_hover: __SEMANTIC_COLOR__.alert500,
  // Border
  buttonPrimary_border: __SEMANTIC_COLOR__.action500,
  buttonSecondary_border: __SEMANTIC_COLOR__.neutral800,
  buttonTertiary_border: __SEMANTIC_COLOR__.neutral400,
  buttonAlert_border: __SEMANTIC_COLOR__.alert500,
  // Background Disabled
  buttonPrimary_disabled: __SEMANTIC_COLOR__.neutral300,
  buttonSecondary_disabled: __SEMANTIC_COLOR__.neutral200,
  buttonTertiary_disabled: __SEMANTIC_COLOR__.neutral200,
  buttonAlert_disabled: __SEMANTIC_COLOR__.neutral200,
  // Border Disabled
  buttonPrimary_border_disabled: __SEMANTIC_COLOR__.neutral300,
  buttonSecondary_border_disabled: __SEMANTIC_COLOR__.neutral300,
  buttonTertiary_border_disabled: __SEMANTIC_COLOR__.neutral300,
  buttonAlert_border_disabled: __SEMANTIC_COLOR__.neutral300,
} as const

/**
 * Feedback
 */
const __FEEDBACK_COLOR__ = {
  // Background
  feedbackAction: __SEMANTIC_COLOR__.action100,
  feedbackAlert: __SEMANTIC_COLOR__.alert100,
  feedbackInfo: __SEMANTIC_COLOR__.info100,
  feedbackNeutral: __SEMANTIC_COLOR__.neutral200,
  feedbackSuccess: __SEMANTIC_COLOR__.success100,
  feedbackWarning: __SEMANTIC_COLOR__.warning100,
  // Border
  feedbackAction_border: __SEMANTIC_COLOR__.action400,
  feedbackAlert_border: __SEMANTIC_COLOR__.alert400,
  feedbackInfo_border: __SEMANTIC_COLOR__.info400,
  feedbackNeutral_border: __SEMANTIC_COLOR__.neutral500,
  feedbackSuccess_border: __SEMANTIC_COLOR__.success400,
  feedbackWarning_border: __SEMANTIC_COLOR__.warning400,
} as const

/**
 * Main
 */
const __MAIN_COLOR__ = {
  // Background
  backdrop: __SEMANTIC_COLOR__.neutral200,
  content: __SEMANTIC_COLOR__.neutral100,
  // Background Hover
  content_hover: __SEMANTIC_COLOR__.neutral200,
  // Background Active
  content_active: __SEMANTIC_COLOR__.neutral300,
  // Border
  content_border: __SEMANTIC_COLOR__.neutral300,
  // Border Hover
  content_border_hover: __SEMANTIC_COLOR__.neutral400,
} as const

/**
 * Text
 */
const __TEXT_COLOR__ = {
  // Normal
  textPrimary: __SEMANTIC_COLOR__.neutral800,
  textSecondary: __SEMANTIC_COLOR__.neutral600,
  textTertiary: __SEMANTIC_COLOR__.neutral500,
  // Special
  textAction: __SEMANTIC_COLOR__.action500,
  textAlert: __SEMANTIC_COLOR__.alert500,
  textDisabled: __SEMANTIC_COLOR__.neutral400,
  textLink: __SEMANTIC_COLOR__.info500,
  textSuccess: __SEMANTIC_COLOR__.success500,
  textWarning: __SEMANTIC_COLOR__.warning500,
  // Button
  textButtonPrimary: __SEMANTIC_COLOR__.neutral100,
  textButtonSecondary: __SEMANTIC_COLOR__.neutral200,
  textButtonTertiary: __SEMANTIC_COLOR__.neutral600,
  textButtonAlert: __SEMANTIC_COLOR__.neutral100,
  // Button Disabled
  textButtonPrimary_disabled: __SEMANTIC_COLOR__.neutral500,
  textButtonSecondary_disabled: __SEMANTIC_COLOR__.neutral400,
  textButtonTertiary_disabled: __SEMANTIC_COLOR__.neutral400,
  textButtonAlert_disabled: __SEMANTIC_COLOR__.neutral400,
} as const

/************************************************************
 * Export
 */
export const COLOR = Object.freeze({
  // Base Colors
  ...__COLOR__,
  ...__SEMANTIC_COLOR__,
  // UI Colors
  ...__BUTTON_COLOR__,
  ...__MAIN_COLOR__,
  ...__FEEDBACK_COLOR__,
  ...__TEXT_COLOR__,
})
